/**
 * CameraG100DeviceSheet (#411) — a G100 camera's "Status and actions", opened from the camera's edit sheet under
 * "Camera settings" (#403), through the owner API that already exists:
 *   GET  /api/cameras/{id}/g100/          presence, the camera's own status report, can_control, allowed_actions
 *   GET  /api/cameras/{id}/g100/actions/  the last 50 action rows
 *   POST /api/cameras/{id}/g100/actions/  one of the six owner actions
 * Everyone who can see the camera sees the status; the action buttons are the ones the server lists in
 * `allowed_actions` (#410: the owner and a superuser all six, a sharee with the settings tick updateGPS and reboot).
 * The two resets (superuser-only in the owner API, so in a superuser's allowed_actions) are never offered here.
 * Every status row is read from the report (`status`), never from the camera object.
 */
import * as Dialog from '@radix-ui/react-dialog'
import { useEffect, useId, useMemo, useState, type ComponentType, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  X, Loader2, Activity, AlertTriangle, Lock, Camera as CameraIcon, Pause, Play, MapPin, RotateCw, HardDrive,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Input } from '@/components/ui/input'
import { g100OwnerApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { toast } from '@/lib/toast'
import { toast as sonnerToast } from 'sonner'
import { cn, formatSDCard } from '@/lib/utils'
import { fieldErrors, statusOf, when } from '@/components/g100/G100SettingControls'
import type {
  Camera, G100OwnerAction, G100OwnerActionCode, G100OwnerActionRequest, G100OwnerStatus, G100Presence,
} from '@/types'

interface CameraG100DeviceSheetProps {
  camera: Camera
  open: boolean
  onOpenChange: (open: boolean) => void
}

// The six owner actions, in the sheet's order. The resets are not among them, for anyone.
const SIX: readonly G100OwnerActionCode[] = ['captureImg', 'pauseCapture', 'unPauseCapture', 'updateGPS', 'reboot', 'formatSDCard']
const OWNER_ACTION: ReadonlySet<string> = new Set(SIX)
type ListedState = 'pending' | 'sent' | 'done' | 'failed' | 'expired'
const LISTED_STATE: ReadonlySet<string> = new Set<ListedState>(['pending', 'sent', 'done', 'failed', 'expired'])
const WAITING_STATE: ReadonlySet<string> = new Set<ListedState>(['pending', 'sent'])
const HISTORY_ROWS = 10

// Copied from the Test Console's StatusCard (G100ConsolePage), not imported.
const PRESENCE_STYLE: Record<G100Presence, string> = {
  online: 'bg-green-500/15 text-green-700 dark:text-green-400',
  asleep: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  overdue: 'bg-red-500/15 text-red-700 dark:text-red-400',
  never: 'bg-muted text-muted-foreground',
}

// Protocol section 9: the four error codes that get plain words; any other is shown as the camera sends it.
const SD_ERRORS = ['SD_FULL', 'SD_NOT_FOUND', 'SD_NOT_SUPPORTED', 'SD_ERR'] as const
type SdError = (typeof SD_ERRORS)[number]
const DETAILS = ['sfVersion', 'displayVersion', 'model', 'fwVersion', 'mcuVersion', 'fwType'] as const
const POWER_TYPES = ['0', '1', '2'] as const
const SOLAR_TYPES = ['0', '1', '2', '3'] as const
const SOLAR_STATUSES = ['0', '1', '2'] as const

// Each row's report fields: status_reported_at uses the camera's own field names (an alert report carries
// only the field that changed, so rows can have different ages).
const SOURCES = {
  battery: ['battery'],
  signal: ['signalStrength'],
  power: ['powerSupplyType', 'supplyVoltage'],
  solar: ['solarPanelType', 'solarPanelStatus', 'solarPanelPower'],
  sd: ['sdCapacity', 'sdFree', 'sdPic'],
  temperature: ['fahrenheit'],
  network: ['carriers'],
} as const
type SourcedRow = keyof typeof SOURCES

/** A number from the report: the server stores values without type checks, so a number can arrive as a string. */
function num(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** One of a field's documented codes (a whole number, possibly sent as a string), else null. */
function code<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  const n = num(value)
  if (n === null || !Number.isInteger(n)) return null
  return (allowed as readonly string[]).includes(String(n)) ? (String(n) as T) : null
}

function text(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() !== '' ? value : null
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

/** The newest of these fields' report times. */
function newest(times: Record<string, unknown>, keys: readonly string[]): string | null {
  let best: string | null = null
  let bestMs = -Infinity
  for (const key of keys) {
    const iso = times[key]
    if (typeof iso !== 'string') continue
    const ms = Date.parse(iso)
    if (Number.isFinite(ms) && ms > bestMs) { best = iso; bestMs = ms }
  }
  return best
}

/** The action codes of the six with a row still waiting (pending or sent) anywhere in the history. */
function waitingActions(rows: G100OwnerAction[] | undefined): ReadonlySet<string> {
  return new Set((rows ?? []).filter((r) => OWNER_ACTION.has(r.action) && WAITING_STATE.has(r.state)).map((r) => r.action))
}

/** A GET hides the sheet only when it has nothing to show yet, or when the camera is gone (404). A failed background
 *  re-read (a 502, a dropped connection) keeps the sheet as last read; the next re-read tries again. */
function unreadable(query: { isError: boolean; data: unknown; error: unknown }): boolean {
  return query.isError && (query.data === undefined || statusOf(query.error) === 404)
}

/** The server's own words for a refused action: the `errors` texts (400), else `detail` (403, 404). */
function serverText(error: unknown): string {
  const data = (error as { response?: { data?: { errors?: unknown; detail?: unknown } } })?.response?.data
  const errors = record(data?.errors)
  if (Object.keys(errors).length > 0) return Object.values(errors).map(String).join('; ')
  if (data?.detail) return String(data.detail)
  return ''
}

export function CameraG100DeviceSheet({ camera, open, onOpenChange }: CameraG100DeviceSheetProps) {
  const { t } = useTranslation('cameras')
  const queryClient = useQueryClient()
  const statusKey = queryKeys.g100Owner.status(camera.id)
  const actionsKey = queryKeys.g100Owner.actions(camera.id)
  const whileWaiting = queryOptions.g100Owner.statusWhileWaitingMs

  // Both re-read while the sheet is open and one of the six waits anywhere in the 50 rows; never when closed.
  const actions = useQuery({
    queryKey: actionsKey,
    queryFn: () => g100OwnerApi.actions(camera.id),
    enabled: open,
    refetchInterval: (query) => (open && waitingActions(query.state.data?.results).size > 0 ? whileWaiting : false),
  })
  const waiting = useMemo(() => waitingActions(actions.data?.results), [actions.data])
  const status = useQuery({
    queryKey: statusKey,
    queryFn: () => g100OwnerApi.status(camera.id),
    enabled: open,
    refetchInterval: open && waiting.size > 0 ? whileWaiting : false,
  })

  const [inFlight, setInFlight] = useState<ReadonlySet<G100OwnerActionCode>>(() => new Set())
  const [minutes, setMinutes] = useState('30')
  const [minutesError, setMinutesError] = useState<string | null>(null)
  const [rebootOpen, setRebootOpen] = useState(false)
  const [formatOpen, setFormatOpen] = useState(false)
  const minutesId = useId()
  const minutesErrorId = `${minutesId}-error`
  const minutesValue = /^\d+$/.test(minutes.trim()) && Number(minutes) >= 1 ? Number(minutes) : null
  // A fresh field each time the sheet opens, as the settings sheet (403) resets its draft.
  useEffect(() => { if (open) { setMinutes('30'); setMinutesError(null) } }, [open, camera.id])

  // The hook-level callbacks run for every POST, so two different actions can be in flight together.
  const send = useMutation({
    mutationFn: (body: G100OwnerActionRequest) => g100OwnerApi.sendAction(camera.id, body),
    onMutate: (body) => {
      setInFlight((s) => new Set(s).add(body.action))
      if (body.action === 'pauseCapture') setMinutesError(null)
    },
    onSuccess: (row, body) => {
      // The new row at once, so its button stays disabled between the 201 and the next read.
      queryClient.setQueryData<{ results: G100OwnerAction[] }>(actionsKey, (old) => ({
        results: [row, ...(old?.results ?? []).filter((r) => r.id !== row.id)],
      }))
      // An earlier refusal of this action has its own toast id (below), so it would stay beside the Queued.
      sonnerToast.dismiss(`g100-action-${body.action}`)
      toast.success(t('g100Device.queued'))
    },
    onError: (error, body) => {
      const errors = fieldErrors(error)
      if (body.action === 'pauseCapture' && errors.minutes) setMinutesError(errors.minutes)
      // Its own id per action: the success toast uses the shared id, so a later Queued would otherwise replace it.
      toast.error(t('g100Device.sendFailedAction', { action: t(`g100Device.actions.${body.action}`) }), serverText(error),
        { id: `g100-action-${body.action}` })
    },
    onSettled: (_row, _error, body) => {
      // Re-read after a refusal too: a 502 or a dropped connection may still have queued the row, and reading it
      // disables the button, so a second tap does not send the command twice.
      queryClient.invalidateQueries({ queryKey: statusKey })
      queryClient.invalidateQueries({ queryKey: actionsKey })
      setInFlight((s) => { const next = new Set(s); next.delete(body.action); return next })
      if (body.action === 'reboot') setRebootOpen(false)
      if (body.action === 'formatSDCard') setFormatOpen(false)
    },
  })
  const post = (body: G100OwnerActionRequest) => send.mutate(body)
  // A button is disabled while its own POST is in flight, and while a row of the same action waits.
  const busy = (action: G100OwnerActionCode) => inFlight.has(action) || waiting.has(action)

  const data = status.data
  const history = actions.data?.results
  // THE gate for the action buttons: the server's allowed_actions (#410), never camera.is_owner, and nothing until
  // the GET has answered (the content below waits for both GETs). An answer without the list (an older API) falls
  // back to can_control: all six or none.
  const listed: unknown = data?.allowed_actions
  const allowed: ReadonlySet<string> = new Set<string>(
    Array.isArray(listed) ? listed : data?.can_control === true ? SIX : [])
  const may = (action: G100OwnerActionCode) => allowed.has(action)
  const showActions = data?.can_control === true || SIX.some(may)
  // A sharee with the settings tick: a line says the rest are the owner's.
  const someOwnerOnly = SIX.some((a) => !may(a))

  const actionButton = (action: G100OwnerActionCode, Icon: ComponentType<{ className?: string }>, onClick: () => void,
    extraDisabled = false) => (
    <Button variant="outline" size="sm" onClick={onClick} disabled={busy(action) || extraDisabled}>
      {inFlight.has(action)
        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        : <Icon className="h-3.5 w-3.5 mr-1.5" />}
      {t(`g100Device.actions.${action}`)}
    </Button>
  )

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-full max-w-xl border-l border-border bg-background shadow-elevation-3 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <Activity className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <Dialog.Title className="text-base font-semibold truncate">{t('g100Device.title')}</Dialog.Title>
                  <Dialog.Description className="text-xs text-muted-foreground truncate">{camera.name} · {camera.imei}</Dialog.Description>
                </div>
              </div>
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" aria-label={t('g100Device.close')}>
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
              {unreadable(status) || unreadable(actions) ? (
                <p className="text-sm text-destructive">{t('g100Device.loadError')}</p>
              ) : !data || !history ? (
                <p className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 mr-2 animate-spin" />…</p>
              ) : (
                <>
                  <PresenceLine data={data} />
                  <StatusSection data={data} />

                  <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('g100Device.actionsTitle')}</h3>
                    {showActions ? (
                      <div className="space-y-4">
                        {may('captureImg') && (
                          <ActionRow hint={t('g100Device.captureHint')}>
                            {actionButton('captureImg', CameraIcon, () => post({ action: 'captureImg' }))}
                          </ActionRow>
                        )}

                        {(may('pauseCapture') || may('unPauseCapture')) && (
                          <ActionRow hint={t('g100Device.pauseHint')}>
                            {may('pauseCapture') && (
                              <>
                                <label htmlFor={minutesId} className="text-sm">{t('g100Device.pauseFor')}</label>
                                <Input
                                  id={minutesId}
                                  type="number"
                                  min={1}
                                  step={1}
                                  inputMode="numeric"
                                  value={minutes}
                                  onChange={(e) => { setMinutes(e.target.value); setMinutesError(null) }}
                                  aria-invalid={minutesError ? true : undefined}
                                  aria-describedby={minutesError ? minutesErrorId : undefined}
                                  className="h-8 w-20"
                                />
                                <span className="text-sm text-muted-foreground">{t('g100Device.minutesUnit')}</span>
                                {actionButton('pauseCapture', Pause,
                                  () => { if (minutesValue !== null) post({ action: 'pauseCapture', minutes: minutesValue }) },
                                  minutesValue === null)}
                              </>
                            )}
                            {may('unPauseCapture') && actionButton('unPauseCapture', Play, () => post({ action: 'unPauseCapture' }))}
                            {minutesError && <p id={minutesErrorId} className="basis-full text-xs text-destructive">{minutesError}</p>}
                          </ActionRow>
                        )}

                        {may('updateGPS') && (
                          <ActionRow hint={t('g100Device.updateLocationHint')}>
                            {actionButton('updateGPS', MapPin, () => post({ action: 'updateGPS' }))}
                          </ActionRow>
                        )}

                        {(may('reboot') || may('formatSDCard')) && (
                          <>
                            <div className="flex items-center gap-3 pt-1">
                              <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{t('g100Device.restartAndStorage')}</span>
                              <div className="h-px flex-1 bg-border" />
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              {may('reboot') && actionButton('reboot', RotateCw, () => setRebootOpen(true))}
                              {may('formatSDCard') && actionButton('formatSDCard', HardDrive, () => setFormatOpen(true))}
                            </div>
                          </>
                        )}

                        {someOwnerOnly && (
                          <div className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                            <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <p>{t('g100Device.otherActionsOwnerOnly')}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                        <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                        <p>{t('g100Device.ownerOnly')}</p>
                      </div>
                    )}
                  </section>

                  <HistorySection rows={history} />
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
              <Dialog.Close asChild>
                <Button variant="outline">{t('g100Device.close')}</Button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ConfirmDialog
        open={rebootOpen}
        onOpenChange={(o) => { if (!o) setRebootOpen(false) }}
        title={t('g100Device.rebootConfirmTitle', { camera: camera.name })}
        description={t('g100Device.rebootConfirmBody')}
        confirmLabel={t('g100Device.rebootConfirmAction')}
        cancelLabel={t('g100Device.cancel')}
        variant="destructive"
        loading={inFlight.has('reboot')}
        // A reboot row read while the confirm is open (another tab, the console) blocks a second command.
        confirmDisabled={waiting.has('reboot')}
        onConfirm={() => { if (may('reboot') && !busy('reboot')) post({ action: 'reboot' }) }}
      />

      {/* Remounted on open and close, so the typed name never carries over. */}
      <FormatConfirmDialog
        key={formatOpen ? 'open' : 'closed'}
        open={formatOpen}
        cameraName={camera.name}
        sending={inFlight.has('formatSDCard')}
        blocked={waiting.has('formatSDCard')}
        onCancel={() => setFormatOpen(false)}
        onConfirm={(name) => { if (may('formatSDCard') && !busy('formatSDCard')) post({ action: 'formatSDCard', confirm_name: name }) }}
      />
    </>
  )
}

function ActionRow({ hint, children }: { hint: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  )
}

function PresenceLine({ data }: { data: G100OwnerStatus }) {
  const { t } = useTranslation('cameras')
  const presence = data.presence
  // By the time, whatever the presence: an Asleep camera in its grace hour is already past its check-in time.
  const expected = data.next_checkin_expected
  const passed = expected !== null && Date.parse(expected) < Date.now()
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      {presence && (
        <span data-testid="g100-presence-chip" className={cn('rounded px-1.5 py-0.5 text-xs font-medium', PRESENCE_STYLE[presence])}>
          {t(`g100Device.presence.${presence}`)}
        </span>
      )}
      {data.last_seen_at && <span>{t('g100Device.lastSeen', { when: when(data.last_seen_at) })}</span>}
      {expected && (
        <span>
          {passed
            ? t('g100Device.checkinWasExpected', { when: when(expected) })
            : t('g100Device.nextCheckin', { when: when(expected) })}
        </span>
      )}
    </div>
  )
}

interface Row { key: SourcedRow | 'lastReport'; label: string; value: string; at: string | null }

function StatusSection({ data }: { data: G100OwnerStatus }) {
  const { t } = useTranslation('cameras')
  const report = record(data.status)
  const times = record(data.status_reported_at)
  const reported = Object.keys(report).length > 0

  const rows: Row[] = []
  const add = (key: SourcedRow, value: string | null) => {
    if (value !== null) rows.push({ key, label: t(`g100Device.rows.${key}`), value, at: newest(times, SOURCES[key]) })
  }
  if (reported) {
    const battery = num(report.battery)
    add('battery', battery !== null && battery >= 0 && battery <= 100 ? `${battery}%` : null)

    const signal = num(data.signal_percent)
    add('signal', signal !== null ? `${signal}%` : null)

    const power = code(report.powerSupplyType, POWER_TYPES)
    const volts = num(report.supplyVoltage)
    add('power', power === null ? null
      : t(`g100Device.powerTypes.${power}`) + (volts !== null ? ` · ${volts.toFixed(1)} V` : ''))

    const solar = code(report.solarPanelType, SOLAR_TYPES)
    let solarValue: string | null = null
    if (solar !== null) {
      const parts = [t(`g100Device.solarTypes.${solar}`)]
      if (solar === '2') {     // a smart panel only: its charge state and its own battery
        const state = code(report.solarPanelStatus, SOLAR_STATUSES)
        const percent = num(report.solarPanelPower)
        if (state !== null) parts.push(t(`g100Device.solarStatus.${state}`))
        if (percent !== null) parts.push(t('g100Device.panelBattery', { percent }))
      }
      solarValue = parts.join(' · ')
    }
    add('solar', solarValue)

    // The server mirror's rule: used only when 0 <= sdFree <= sdCapacity.
    const capacity = num(report.sdCapacity)
    const free = num(report.sdFree)
    const sd = capacity !== null && free !== null && free >= 0 && free <= capacity ? formatSDCard(capacity - free, capacity) : null
    const files = num(report.sdPic)
    add('sd', sd === null ? null : sd + (files !== null ? ` · ${t('g100Device.files', { count: files })}` : ''))

    // The server mirror's formula.
    const fahrenheit = num(report.fahrenheit)
    add('temperature', fahrenheit === null ? null : `${Math.round(((fahrenheit - 32) * 5) / 9 * 10) / 10}°C`)

    add('network', text(report.carriers))

    const last = newest(times, Object.keys(times))
    if (last) rows.push({ key: 'lastReport', label: t('g100Device.rows.lastReport'), value: when(last), at: null })
  }

  const errStatus = typeof report.errStatus === 'string' && report.errStatus.trim() !== '' ? report.errStatus : null
  const errAt = typeof times.errStatus === 'string' ? times.errStatus : null
  const details = DETAILS.flatMap((key) => {
    const value = text(report[key])
    return value === null ? [] : [{ key, value }]
  })

  return (
    <>
      {errStatus && (
        <div data-testid="g100-error-banner"
          className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <p className="space-x-1">
            <span className="font-medium">
              {(SD_ERRORS as readonly string[]).includes(errStatus) ? t(`g100Device.errors.${errStatus as SdError}`) : errStatus}
            </span>
            {errAt && <span className="text-xs">{t('g100Device.errorReportedAt', { when: when(errAt) })}</span>}
          </p>
        </div>
      )}

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('g100Device.statusTitle')}</h3>
        {!reported ? (
          <p className="text-sm text-muted-foreground">{t('g100Device.noReport')}</p>
        ) : (
          <dl className="divide-y divide-border/40">
            {rows.map((row) => (
              <div key={row.key} data-testid={`g100-row-${row.key}`}
                title={row.at ? t('g100Device.reportedAt', { when: when(row.at) }) : undefined}
                className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd className="text-right">{row.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {details.length > 0 && (
          <details className="pt-1">
            <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground">{t('g100Device.moreDetails')}</summary>
            <dl className="mt-2 divide-y divide-border/40">
              {details.map(({ key, value }) => (
                <div key={key} className="flex items-baseline justify-between gap-3 py-1 text-xs">
                  <dt className="text-muted-foreground">{t(`g100Device.details.${key}`)}</dt>
                  <dd className="text-right font-mono break-all">{value}</dd>
                </div>
              ))}
            </dl>
          </details>
        )}
      </section>
    </>
  )
}

const STATE_STYLE: Record<ListedState, string> = {
  pending: 'text-amber-600 dark:text-amber-400',
  sent: 'text-amber-600 dark:text-amber-400',
  done: 'text-emerald-600 dark:text-emerald-400',
  failed: 'text-destructive',
  expired: 'text-muted-foreground',
}

function HistorySection({ rows }: { rows: G100OwnerAction[] }) {
  const { t } = useTranslation('cameras')
  // Only the six codes in the five states, newest first (the server's order), at most 10.
  const listed = rows
    .filter((r) => OWNER_ACTION.has(r.action) && LISTED_STATE.has(r.state))
    .slice(0, HISTORY_ROWS)
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('g100Device.historyTitle')}</h3>
      {listed.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('g100Device.noActions')}</p>
      ) : (
        <ul className="divide-y divide-border/40">
          {listed.map((row) => {
            const action = row.action as G100OwnerActionCode
            const state = row.state as ListedState
            const minutes = action === 'pauseCapture' ? num(record(row.params).minutes) : null
            const answered = (state === 'done' || state === 'failed') && row.answered_at
            return (
              <li key={row.id} data-testid="g100-action-row" className="py-2 text-sm">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="font-medium">
                    {t(`g100Device.actions.${action}`)}
                    {minutes !== null && <span className="font-normal text-muted-foreground"> · {t('g100Device.pauseMinutes', { count: minutes })}</span>}
                  </span>
                  <span className={cn('text-xs', STATE_STYLE[state])}>
                    {state === 'failed'
                      ? t('g100Device.states.failed', { code: row.result_code ?? '?' })
                      : t(`g100Device.states.${state}`)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('g100Device.requestedAt', { when: when(row.created_at) })}
                  {answered && <> · {t('g100Device.answeredAt', { when: when(row.answered_at) })}</>}
                </p>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

/** The format's typed-name confirm: Confirm stays disabled until the camera's name is typed exactly (the server
 *  compares it with the stored name, exactly, and answers 400 otherwise). The console's TypedNameDialog is not
 *  shared: its texts are the console's own (#411 builder's choice, see the item's build record). */
function FormatConfirmDialog({ open, cameraName, sending, blocked, onCancel, onConfirm }: {
  open: boolean
  cameraName: string
  sending: boolean
  /** A format row of this camera is pending or sent: Confirm stays disabled (no spinner), Cancel still closes. */
  blocked: boolean
  onCancel: () => void
  onConfirm: (name: string) => void
}) {
  const { t } = useTranslation('cameras')
  const [typed, setTyped] = useState('')
  const inputId = useId()
  const ready = typed === cameraName && !sending && !blocked
  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next && !sending) onCancel() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 space-y-4 rounded-xl border border-border bg-background p-6 shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <Dialog.Title className="text-lg font-semibold">{t('g100Device.formatConfirmTitle')}</Dialog.Title>
          </div>
          <Dialog.Description className="text-sm text-muted-foreground">
            {t('g100Device.formatConfirmBody', { camera: cameraName })}
          </Dialog.Description>
          <form className="space-y-1" onSubmit={(e) => { e.preventDefault(); if (ready) onConfirm(typed) }}>
            <label htmlFor={inputId} className="text-xs font-medium">
              {t('g100Device.formatConfirmLabel')} <span className="font-mono">({cameraName})</span>
            </label>
            <Input id={inputId} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoFocus disabled={sending} />
          </form>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onCancel} disabled={sending}>{t('g100Device.cancel')}</Button>
            <Button variant="destructive" className="flex-1" disabled={!ready} onClick={() => onConfirm(typed)}>
              {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('g100Device.formatConfirmAction')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
